import { Flex } from "antd";
import { useCallback, useMemo, useState } from "react";

import { useProtectedUserRoleSelector } from "@/hooks";
import { Header } from "@/shared/layout";
import { MainContainer } from "@/shared/layout";
import { BlockList } from "@/shared/pageComposition";
import { Meta } from "@/shared/seo";

import { getMainInformationBlocks } from "./MainInformation.blocks";
import { buildMainInformationModel } from "./MainInformation.model";
import { getMainInformationCapabilities } from "./MainInformation.policy";
import { CameraModeModal } from "./modals/CameraModeModal";
import { ChangePhotoModal } from "./modals/ChangePhotoModal";

type MainInformationModal = "cameraMode" | "changePhoto" | null;

export const MainInformationPage = () => {
    const role = useProtectedUserRoleSelector();
    const capabilities = getMainInformationCapabilities(role);

    const [modal, setModal] = useState<MainInformationModal>(null);

    const handleCloseModal = useCallback(() => {
        setModal(null);
    }, []);

    const handleOpenChangePhotoModal = useCallback(() => {
        setModal("changePhoto");
    }, []);

    const handleOpenChangeCameraModal = useCallback(() => {
        setModal("cameraMode");
    }, []);

    const model = useMemo(() => {
        return buildMainInformationModel({
            capabilities,
            onOpenChangePhoto: handleOpenChangePhotoModal,
            onOpenCameraMode: handleOpenChangeCameraModal,
        });
    }, [capabilities, handleOpenChangePhotoModal, handleOpenChangeCameraModal]);

    const blocks = useMemo(() => getMainInformationBlocks(model), [model]);

    return (
        <>
            <Meta title="Profile" />
            <Header withMidnightCountdown />

            <MainContainer>
                <Flex vertical gap={0}>
                    <BlockList blocks={blocks} model={model} />
                </Flex>
            </MainContainer>

            <CameraModeModal
                open={modal === "cameraMode"}
                onClose={handleCloseModal}
            />

            <ChangePhotoModal
                open={modal === "changePhoto"}
                onClose={handleCloseModal}
            />
        </>
    );
};

export default MainInformationPage;
